import { z } from "zod";
import { calculationItemInputSchema, calculationItemResultSchema, calculationLotSchema, rulesetSelectorSchema, taxIssueCodeSchema, } from "./calculation.js";
import { artifactRefSchema, cursorPageSchema, environmentSchema } from "./context.js";
export const fiscalTaskTypeSchema = z.literal("LOT_CALCULATION");
export const fiscalTaskStateSchema = z.enum([
    "QUEUED",
    "RUNNING",
    "WAITING_REVIEW",
    "SUCCEEDED",
    "FAILED",
    "CANCELLED",
]);
export const fiscalTaskLotSchema = calculationLotSchema;
const fiscalTaskSnapshotSchema = z
    .object({
    contentSha256: z.string().regex(/^[a-f0-9]{64}$/),
    rowCount: z.number().int().positive().max(5_000),
    artifact: artifactRefSchema.nullable().optional(),
    items: z.array(calculationItemInputSchema).min(1).max(5_000),
})
    .strict()
    .superRefine((snapshot, context) => {
    if (snapshot.rowCount !== snapshot.items.length) {
        context.addIssue({
            code: "custom",
            path: ["rowCount"],
            message: "rowCount must match items.length",
        });
    }
    const ids = new Set();
    const positions = new Set();
    for (const item of snapshot.items) {
        if (ids.has(item.itemId)) {
            context.addIssue({ code: "custom", path: ["items"], message: "itemId must be unique" });
        }
        if (positions.has(item.position)) {
            context.addIssue({
                code: "custom",
                path: ["items"],
                message: "position must be unique",
            });
        }
        ids.add(item.itemId);
        positions.add(item.position);
    }
});
export const fiscalTaskCreateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    taskType: fiscalTaskTypeSchema,
    workflowId: z.string().trim().min(1).max(100),
    calculationDate: z.string().date(),
    rulesetSelector: rulesetSelectorSchema,
    lot: fiscalTaskLotSchema,
    snapshot: fiscalTaskSnapshotSchema,
    maxAttempts: z.number().int().min(1).max(5).default(3),
})
    .strict();
const digestSchema = z.string().regex(/^sha256:[a-f0-9]{64}$/);
/**
 * Why an uploaded lot was calculated again. CURRENT_ENGINE: the same published ruleset version with
 * the engine and engine opt-ins the calculator sends today (purchase lines, PIS/COFINS profiles, the
 * route rules the platform resolves). RULESET: another published ruleset version.
 * RESEARCH: server-verified research changed the applicable route facts; the published
 * version may be unchanged. Public upload routes cannot assert this machine provenance.
 */
export const lotRecalculationBasisSchema = z.enum(["CURRENT_ENGINE", "RULESET", "RESEARCH"]);
/** What a person asks for when recalculating an uploaded lot task. */
export const lotRecalculationRequestSchema = z
    .object({
    /** The original task; it is never changed. */
    sourceTaskId: z.string().uuid(),
    basis: lotRecalculationBasisSchema,
    /** The person's note on why, kept with the audit record. */
    reason: z.string().trim().min(1).max(500).optional(),
})
    .strict();
/**
 * The recalculation a task records (who: `createdBy`). Absent on a task that is not a
 * recalculation, and on every task created before it existed.
 */
export const fiscalTaskRecalculationSchema = z
    .object({
    sourceTaskId: z.string().uuid(),
    basis: lotRecalculationBasisSchema,
    reason: z.string().min(1).max(500).nullable(),
    /** The original task's pinned ruleset version. */
    sourceRulesetVersion: z.string().min(1).max(80),
})
    .strict();
export const fiscalTaskSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    id: z.string().uuid(),
    taskType: fiscalTaskTypeSchema,
    status: fiscalTaskStateSchema,
    environment: environmentSchema,
    workflowId: z.string().min(1).max(100),
    organizationId: z.string().uuid(),
    requestId: z.string().min(1).max(128),
    correlationId: z.string().min(1).max(128),
    calculationDate: z.string().date(),
    lot: fiscalTaskLotSchema,
    snapshot: z
        .object({
        contentSha256: z.string().regex(/^[a-f0-9]{64}$/),
        rowCount: z.number().int().positive().max(5_000),
        artifact: artifactRefSchema.nullable(),
    })
        .strict(),
    ruleset: z
        .object({
        selector: rulesetSelectorSchema,
        version: z.string().min(1).max(80).nullable(),
        contentDigest: digestSchema.nullable(),
    })
        .strict(),
    progress: z
        .object({
        itemCount: z.number().int().nonnegative(),
        chunkCount: z.number().int().positive(),
        completedChunkCount: z.number().int().nonnegative(),
        failedChunkCount: z.number().int().nonnegative(),
        calculatedCount: z.number().int().nonnegative(),
        reviewRequiredCount: z.number().int().nonnegative(),
        percent: z.number().int().min(0).max(100),
        issueCounts: z.partialRecord(taxIssueCodeSchema, z.number().int().nonnegative()),
    })
        .strict(),
    attempts: z
        .object({ current: z.number().int().nonnegative(), max: z.number().int().positive() })
        .strict(),
    lastError: z
        .object({
        code: z.string().min(1).max(80),
        message: z.string().min(1).max(1_000),
        retryable: z.boolean(),
    })
        .strict()
        .nullable(),
    cancelRequestedAt: z.string().datetime().nullable(),
    resultDigest: digestSchema.nullable(),
    createdBy: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
    startedAt: z.string().datetime().nullable(),
    completedAt: z.string().datetime().nullable(),
    updatedAt: z.string().datetime(),
    revision: z.number().int().positive(),
    recalculation: fiscalTaskRecalculationSchema.optional(),
})
    .strict();
export const fiscalTaskPageSchema = cursorPageSchema
    .extend({
    items: z.array(fiscalTaskSchema),
    totalEstimate: z.number().int().nonnegative(),
})
    .strict();
const warningRateSchema = z.string().regex(/^(0|[1-9]\d{0,5})(\.\d{1,8})?$/);
/**
 * A non-blocking warning on a calculated item, derived when the stored result
 * is read (never part of the stored result or its engine version, never a
 * change to an amount). `coverage_needs_review`: the rule in force for the
 * route is flagged NEEDS_REVIEW by the coverage report, for example a
 * wildcard-origin rule whose stated interstate ICMS rate contradicts Res. SF
 * 22/89 for the item's real origin.
 */
export const fiscalTaskItemWarningSchema = z
    .object({
    code: z.literal("coverage_needs_review"),
    reason: z.literal("INTERSTATE_RATE_MISMATCH"),
    message: z.string().min(1).max(500),
    matchedRuleId: z.string().uuid(),
    interstateRate: z.object({ stated: warningRateSchema, expected: warningRateSchema }).strict(),
})
    .strict();
/**
 * Why a REVIEW_REQUIRED item needs review and what would fix it, one entry per issue code, derived
 * when the stored result is read (never stored, never part of a digest). `reason` and `params` are
 * stable codes the console translates; `message` is the same in English. `fix` is present only when
 * it can be derived from the published rules or the file (a CEST a rule states for the NCM and
 * route, a field to fill, a treatment to state); it is a suggestion the person confirms.
 */
const cestCodeSchema = z.string().regex(/^\d{2}\.\d{3}\.\d{2}$/);
export const reviewReasonSchema = z.enum([
    "CEST_MISMATCH",
    "AMBIGUOUS_CEST",
    "NO_RULE_FOR_ROUTE",
    "NO_RULE_FOR_NCM",
    "ICMS_NOT_PUBLISHED",
    "RULE_EXPIRED",
    "RULE_NOT_YET_EFFECTIVE",
    "RULESET_EMPTY",
    "FIELD_MISSING",
    "PROFILE_PENDING",
    "BENEFIT_NOT_IN_RULE",
    "SALE_RULE_MISSING",
    "OTHER",
]);
export const reviewFixSchema = z.discriminatedUnion("kind", [
    /** Exactly one CEST fits: "Corrigir CEST e recalcular". */
    z
        .object({ kind: z.literal("SET_CEST"), value: cestCodeSchema })
        .strict(),
    /** Several CESTs fit: the person picks one ("Reparar planilha"). */
    z
        .object({ kind: z.literal("PICK_CEST"), values: z.array(cestCodeSchema).min(2).max(20) })
        .strict(),
    /** No rule states the route: research it (or publish its rule). */
    z
        .object({ kind: z.literal("RESEARCH_ROUTE") })
        .strict(),
    /** A value missing in the file: fill the column and import again. */
    z
        .object({ kind: z.literal("FIX_SHEET"), field: z.string().min(1).max(40) })
        .strict(),
    /** State the PIS/COFINS treatment or the profile the product needs. */
    z
        .object({ kind: z.literal("STATE_TREATMENT") })
        .strict(),
]);
export const reviewDetailSchema = z
    .object({
    code: taxIssueCodeSchema,
    reason: reviewReasonSchema,
    message: z.string().min(1).max(500),
    params: z.record(z.string().max(40), z.string().max(200)),
    fix: reviewFixSchema.optional(),
})
    .strict();
export const fiscalTaskResultSchema = z
    .object({
    taskId: z.string().uuid(),
    itemId: z.string().min(1),
    position: z.number().int().nonnegative(),
    chunkIndex: z.number().int().nonnegative(),
    calculationId: z.string().uuid(),
    input: calculationItemInputSchema,
    result: calculationItemResultSchema,
    /** Stored intermediate values from the engine audit; absent on legacy records. */
    memory: z
        .object({
        stBase: z.string().nullable(),
        ownIcms: z.string().nullable(),
        /** True ST from the audit tape. Legacy amounts.icmsSt may hold standard ICMS. */
        stAmount: z
            .string()
            .regex(/^\d+\.\d{2}$/)
            .nullable()
            .optional(),
    })
        .strict()
        .optional(),
    /** Only on a calculated item that carries a warning; derived at read time. */
    warnings: z.array(fiscalTaskItemWarningSchema).max(5).optional(),
    /** Only on a REVIEW_REQUIRED item: why, and the fix when one is derivable; read time. */
    reviewDetails: z.array(reviewDetailSchema).max(10).optional(),
    completedAt: z.string().datetime(),
})
    .strict();
export const fiscalTaskResultPageSchema = cursorPageSchema
    .extend({
    items: z.array(fiscalTaskResultSchema),
    totalEstimate: z.number().int().nonnegative(),
})
    .strict();
export const fiscalTaskRetrySchema = z
    .object({
    reason: z.string().trim().min(10).max(1_000).optional(),
})
    .strict();
//# sourceMappingURL=fiscal-tasks.js.map